import { Injectable } from '@angular/core';
import {
  CmsNavigationComponent,
} from '@spartacus/core';
import { NavigationService } from '@spartacus/storefront';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { UigNavigationNode } from '../components/uig-navigation/uig-navigation-node-model';


@Injectable({
  providedIn: 'root'
})
export class UigNavigationService extends NavigationService{

  public getNavigationNode(
    data$: Observable<CmsNavigationComponent>
  ): Observable<UigNavigationNode> {
    if (!data$) {
      return of();
    }
    return data$.pipe(
      filter((data) => !!data),
      switchMap((data) => {
        const navigation = data.navigationNode ? data.navigationNode : data;
        return this.cmsService.getNavigationEntryItems(navigation.uid).pipe(
          tap((items) => {
            if (items === undefined) {
              this.customLoadNavigationEntryItems(navigation, true);
            } else {
              // we should check whether the existing node items are what expected

              const expectedItems = [];
              this.customLoadNavigationEntryItems(navigation, false, expectedItems);
              const existingItems = Object.keys(items).map(
                (key) => items[key].uid
              );
              const missingItems = expectedItems.filter(
                (it) => !existingItems.includes(it.id)
              );
              if (missingItems.length > 0) {
                this.cmsService.loadNavigationItems(
                  navigation.uid,
                  missingItems
                );
              }
            }
          }),
          filter(Boolean),
          map((items) => this.customPopulateNavigationNode(navigation, items))
        );
      })
    );
  }

  private customLoadNavigationEntryItems(
    nodeData: any,
    root: boolean,
    itemsList = []
  ): void {
    if (nodeData.entries && nodeData.entries.length > 0) {
      nodeData.entries.forEach((entry) => {
        itemsList.push({
          superType: entry.itemSuperType,
          id: entry.itemId,
        });
      });
    }

    if (nodeData.children && nodeData.children.length > 0) {
      nodeData.children.forEach((child) =>
        this.customLoadNavigationEntryItems(child, false, itemsList)
      );
    }

    if (root) {
      this.cmsService.loadNavigationItems(nodeData.uid, itemsList);
    }
  }

  private customPopulateNavigationNode(nodeData: any, items: any): UigNavigationNode {
    const node: UigNavigationNode = {};

    if (nodeData.title) {
      // the node title will be populated by the first entry (if any)
      // if there's no nodeData.title available
      node.title = nodeData.title;
    }

    // populate style classes to apply CMS driven styling
    if (nodeData.styleClasses) {
      node.styleClasses = nodeData.styleClasses;
    }
    // populate style attributes to apply CMS driven styling
    if (nodeData.styleAttributes) {
      node.styleAttributes = nodeData.styleAttributes;
    }

    if (nodeData.entries && nodeData.entries.length > 0) {
      this.customPopulateLink(node, nodeData.entries[0], items);
    }

    if (nodeData.children?.length > 0) {
      const children = nodeData.children
        .map((child) => this.customPopulateNavigationNode(child, items))
        .filter(Boolean);
      if (children.length > 0) {
        node.children = children;
      }
    }

    node.restricted = nodeData.restricted;
    // return null in case there are no children
    return Object.keys(node).length === 0 ? null : node;
  }

  private customPopulateLink(node: UigNavigationNode, entry, items) {
    const item = items[`${entry.itemId}_${entry.itemSuperType}`];

    // now we only consider CMSLinkComponent
    if (item && (entry.itemType === 'CMSLinkComponent' ||  entry.itemType === 'UigCMSLinkComponent')) {
      if (!node.title) {
        node.title = item.linkName;
      }
      const url = this.getLink(item);
      // only populate the node link if we have a visible node
      if (node.title && url) {
        node.url = url;
        // the backend provide boolean value for the target
        // in case the link should be opened in a new window
        if (item.target === 'true' || item.target === true) {
          node.target = '_blank';
        }
      }
      // populate style classes to apply CMS driven styling
      if (item.styleClasses) {
        node.styleClasses = item.styleClasses;
      }
      // populate style attributes to apply CMS driven styling
      if (item.styleAttributes) {
        node.styleAttributes = item.styleAttributes;
      }
    }

    if (item && entry.itemType === 'CMSParagraphComponent') {
        node.isParagraphComponent = true;
        node.content = item.content;
        if (item.styleClasses) {
          node.styleClasses = item.styleClasses;
        }
    }
  }
}
